import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

function AnalyticsContent() {
  const { t } = useTranslation();
  const { T, S } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    api.getManagerAnalytics().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  const chartTooltipStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    padding: '10px 14px',
    fontSize: '13px',
    fontFamily: "'DM Sans', sans-serif",
    color: T.text,
  };

  const periodTabs = [
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'year', label: 'This Year' },
  ];

  if (loading) return (
    <AdminLayout role="manager">
      <Skeleton count={2} height="200px" />
    </AdminLayout>
  );
  if (!data) return (
    <AdminLayout role="manager">
      <p style={{ color: T.muted }}>{t('manager.analyticsFailed')}</p>
    </AdminLayout>
  );

  const topArtists = (data.bookings_by_artist || [])
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, 8);
  const maxArtistCount = Math.max(...topArtists.map(a => a.count || 0), 1);

  return (
    <AdminLayout role="manager">
      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={S.pageTitle}>{t('admin.analytics')}</h1>
        <div style={{ width: '40px', height: '3px', background: T.orange, borderRadius: '2px', marginTop: '0.3rem' }} />
      </div>

      {/* Period selector */}
      <div style={{
        display: 'inline-flex',
        background: '#f1f5f9',
        borderRadius: '10px',
        padding: '4px',
        marginBottom: '1.5rem',
        gap: '2px',
      }}>
        {periodTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setPeriod(tab.id)}
            style={{
              padding: '0.45rem 1.1rem',
              borderRadius: '8px',
              border: 'none',
              background: period === tab.id ? '#ffffff' : 'transparent',
              color: period === tab.id ? T.orange : T.muted,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: period === tab.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Revenue Trend — Area Chart */}
        <div style={{ ...S.card, padding: '1.5rem' }}>
          <h2 style={{
            fontFamily: "'Sora', sans-serif", fontSize: '0.95rem', fontWeight: 600,
            color: T.white, marginBottom: '1rem',
          }}>
            Revenue Trend
          </h2>
          <div style={{ height: '240px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.revenue_by_date || []} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={T.orange} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={T.orange} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" stroke={T.muted} fontSize={11} tickLine={false} />
                <YAxis stroke={T.muted} fontSize={11} tickLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => [`EGP ${Number(v).toFixed(2)}`, 'Revenue']} />
                <Area type="monotone" dataKey="revenue" stroke={T.orange} strokeWidth={2.5} fillOpacity={1} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bookings by Date — Bar Chart */}
        <div style={{ ...S.card, padding: '1.5rem' }}>
          <h2 style={{
            fontFamily: "'Sora', sans-serif", fontSize: '0.95rem', fontWeight: 600,
            color: T.white, marginBottom: '1rem',
          }}>
            {t('manager.bookingsByDate')}
          </h2>
          <div style={{ height: '240px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.bookings_by_date || []} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" stroke={T.muted} fontSize={11} tickLine={false} />
                <YAxis stroke={T.muted} fontSize={11} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill={T.orange} radius={[6, 6, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Artists — Ranked List */}
      <div style={{ ...S.card, padding: '1.5rem' }}>
        <h2 style={{
          fontFamily: "'Sora', sans-serif", fontSize: '0.95rem', fontWeight: 600,
          color: T.white, marginBottom: '1.25rem',
        }}>
          {t('manager.bookingsByWorker') || 'Top Artists'}
        </h2>
        {topArtists.length === 0 ? (
          <p style={{ color: T.muted, fontSize: '0.9rem' }}>No data available</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {topArtists.map((artist, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{
                  fontFamily: "'Sora', sans-serif", fontSize: '0.8rem', fontWeight: 700,
                  color: idx < 3 ? T.orange : T.muted,
                  width: '24px', textAlign: 'right', flexShrink: 0,
                }}>
                  {idx + 1}
                </span>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: `${T.orange}15`, color: T.orange,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {(artist.artist_name || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 600,
                    color: T.white, marginBottom: '0.3rem',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {artist.artist_name}
                  </div>
                  <div style={{
                    height: '6px', borderRadius: '3px', background: '#f1f5f9',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${(artist.count / maxArtistCount) * 100}%`,
                      height: '100%', borderRadius: '3px',
                      background: `linear-gradient(90deg, ${T.orange}, ${T.orange}99)`,
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
                <span style={{
                  fontFamily: "'Sora', sans-serif", fontSize: '0.85rem', fontWeight: 700,
                  color: T.white, flexShrink: 0,
                }}>
                  {artist.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default function Analytics() {
  return <ProtectedRoute requireSalonManager><AnalyticsContent /></ProtectedRoute>;
}
